import { NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';

import { MatSlider, MatSliderThumb } from '@angular/material/slider';
import { MatTooltip } from '@angular/material/tooltip';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { StyleDirective } from '@ngbracket/ngx-layout/extended';
import { FlexDirective, LayoutAlignDirective, LayoutDirective } from '@ngbracket/ngx-layout/flex';
import * as _ from 'lodash';

import { InsightImage } from '../models/interfaces/insight-image';

@Component({
  selector: 'bt-insight-image',
  templateUrl: './insight-image.component.html',
  styleUrls: ['./insight-image.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FlexDirective,
    FormsModule,
    LayoutAlignDirective,
    LayoutDirective,
    MatIcon,
    MatSlider,
    MatSliderThumb,
    MatTooltip,
    NgStyle,
    ReactiveFormsModule,
    StyleDirective
  ]
})
export class InsightImageComponent implements OnInit {
  public static BRIGHTNESS_MAX = 450;
  public static BRIGHTNESS_MIN = 50;
  public static BRIGHTNESS_DEFAULT = 130;
  public static HIGH_CONTRAST = 110;
  public static LOW_CONTRAST = 90;

  public readonly brightnessMax = InsightImageComponent.BRIGHTNESS_MAX;
  public readonly brightnessMin = InsightImageComponent.BRIGHTNESS_MIN;

  @ViewChild('insightImageContainer', { static: true }) insightImageContainer: ElementRef;
  @ViewChild('insightImage', { static: true }) insightImage: ElementRef;
  @ViewChild('brightnessSlider') brightnessSlider: MatSlider;

  @Input() set brightness(value: number) {
    this._brightness = value;
  }

  get brightness(): number {
    return this._brightness;
  }

  @Input() set contrast(value: number) {
    this._contrast = value;
  }

  get contrast(): number {
    return this._contrast;
  }

  @Input() set brightnessControlVisible(value: boolean) {
    this._brightnessControlVisible = value;
  }

  get brightnessControlVisible(): boolean {
    return this._brightnessControlVisible;
  }

  @Input() set clickToZoom(value: boolean) {
    this._clickToZoom = value;
    if (value === false) {
      this.insightImageContainer.nativeElement.style.cursor = 'pointer';
    }
  }

  get clickToZoom(): boolean {
    return this._clickToZoom;
  }

  @Input() set imageSrc(value: string) {
    this.setImageUrl(value);
  }

  @Input() set insightImageSrc(value: InsightImage) {
    this.setImageUrl(value && value.getImageUrl(), value);
  }

  @Output() imageComplete: EventEmitter<number> = new EventEmitter<number>();

  insightObjectOptions: any = {
    surroundingOpacity: 0.5,
    opacity: 0.7,
    strokeWidth: 10,
    stroke: 'greenyellow'
  };

  public imageUrl: string | null;
  public loadingImage = false;
  public imageStatus: string | null;
  public insightObjectsImageUrl: string | null;
  public filter: SafeStyle;
  public scaleFactor: number;
  public hideNavControls = false;

  protected lastValidUrl = '';
  protected _clickToZoom = true;
  protected _brightnessControlVisible = false;

  _brightness = InsightImageComponent.BRIGHTNESS_DEFAULT;
  _contrast = InsightImageComponent.HIGH_CONTRAST;

  transformOrigin: string;

  constructor(private sanitizer: DomSanitizer) {
  }

  ngOnInit() {
    this.scaleFactor = 1;
    this.transformOrigin = '100% 100%';
    this.setFilter();
  }

  setInsightImage(insightImage: InsightImage | undefined) {
    if (insightImage && insightImage.getImageStatus() === 'realized') {
      this.imageStatus = insightImage.getImageStatus();
      this.setImageUrl(insightImage.getImageUrl(), insightImage);
    }
    else {
      this.setImageUrl(null);
    }
  }

  setImageUrl(url: string | null, insightImage?: InsightImage) {
    this.setZoomMode(false);

    if (url) {
      this.loadingImage = true;

      const image = new Image();
      this.lastValidUrl = url;
      this.imageUrl = null;
      this.insightObjectsImageUrl = null;

      image.onload = () => {
        if (url === this.lastValidUrl) {
          this.imageUrl = url;
          this.loadingImage = false;
          if (insightImage) {
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = image.width;
            tempCanvas.height = image.height;

            this.imageComplete.emit(image.width / image.height);

            const tempContext = tempCanvas.getContext('2d');

            if (tempContext) {
              if (insightImage.getObjectRects().length > 0) {
                tempContext.globalAlpha = this.insightObjectOptions.surroundingOpacity;
                tempContext.fillRect(0, 0, image.width, image.height);
              }

              _.forEach(insightImage.getObjectRects(), (objectRect) => {
                tempContext.globalAlpha = this.insightObjectOptions.opacity;
                tempContext.strokeStyle = insightImage.getObjectColor();
                tempContext.lineWidth = this.insightObjectOptions.strokeWidth;
                tempContext.beginPath();

                objectRect.forEach((point, index) => {
                  if (index === 0) {
                    tempContext.moveTo(point.x, point.y);
                  }
                  else {
                    tempContext.lineTo(point.x, point.y);
                  }
                });

                tempContext.closePath();
                tempContext.save();
                tempContext.clip();
                tempContext.clearRect(0, 0, image.width, image.height);
                tempContext.stroke();
                tempContext.restore();
              });
            }

            this.insightObjectsImageUrl = tempCanvas.toDataURL();
          }
          else {
            this.insightObjectsImageUrl = null;
          }
        }
      };

      image.onerror = () => {
        this.loadingImage = false;
        this.imageUrl = null;
      };

      image.src = url;
    }
    else {
      this.imageUrl = null;
      this.imageStatus = null;
      this.insightObjectsImageUrl = null;
    }
  }

  setZoomMode(zoom: boolean) {
    if (this.clickToZoom === true) {
      this.scaleFactor = zoom === true ? 3 : 1;
      this.hideNavControls = zoom;

      zoom === true
        ? (this.insightImageContainer.nativeElement.style.cursor = 'zoom-out')
        : (this.insightImageContainer.nativeElement.style.cursor = 'zoom-in');
    }
  }

  setFilter() {
    this.filter = this.sanitizer.bypassSecurityTrustStyle(
      'contrast(' + this.contrast + '%) brightness(' + this.brightness + '%)'
    );
  }

  setBrightness(value: number) {
    this.brightness = value;

    if (this.brightness > 300) {
      this.contrast = InsightImageComponent.LOW_CONTRAST;
    }
    else {
      this.contrast = InsightImageComponent.HIGH_CONTRAST;
    }
    this.setFilter();
  }

  mouseDown() {
    if (this.clickToZoom === true) {
      this.scaleFactor === 1 ? this.setZoomMode(true) : this.setZoomMode(false);
    }
  }

  mouseMove(event: MouseEvent) {
    if (this.clickToZoom) {
      const x =
        (event.offsetX / this.insightImageContainer.nativeElement.clientWidth) *
        100;
      const y =
        (event.offsetY /
          this.insightImageContainer.nativeElement.clientHeight) *
        100;

      this.transformOrigin = '' + x + '% ' + y + '%';
    }
  }
}
